<template>
    <div>
        <loader v-if="loading.brandNetworks" />
        <div v-else>
            <div v-for="j in jobs" :key="j.id" class="box box-dashed">
                <div class="content">
                    <network-icon v-if="j.task.network" :network="j.task.network.slug" />
                    <strong>{{ j.task.type }}</strong>

                    <div
                        v-if="j.task.requirements"
                        class="work-room--task-req mt-15"
                        v-html="$sanitize(j.task.requirements)"
                    ></div>
                </div>

                <div v-if="deliverable" class="work-room--task-right">
                    <div
                        v-if="j.task.media_type === 'story' && $root.hasOAuthToken(j.task.network_id)"
                        class="notification is-success is-inverted"
                    >
                        Your Instagram story media and insights will be delivered automatically, there is no further
                        action needed for this task.
                    </div>

                    <div v-if="j.task.media_type === 'bio' || j.task.media_type === 'highlight'"></div>

                    <div v-else-if="canShowDeliver(j)" class="work-room--task-deliver-btn">
                        <div v-if="j.status === 'Declined'" class="mb-15">
                            <span class="tag is-medium is-danger">Declined</span>
                            <div v-if="j.deliverable_status_info" class="mt-5">
                                <strong>Reason:</strong> {{ j.deliverable_status_info }}
                            </div>
                        </div>

                        <div v-if="j.status === 'Pending'" class="mb-15">
                            <span class="tag is-medium is-primary">Pending</span>
                        </div>

                        <button v-if="can_deliver" class="button" @click="showDeliver(j)">
                            <span v-if="delivering && delivering.id === j.id">Cancel Delivery</span>
                            <span v-else-if="j.status === 'Declined'">Redeliver</span>
                            <span v-else-if="j.status === 'Pending'">Change</span>
                            <span v-else>Deliver</span>
                        </button>
                    </div>

                    <div v-else-if="j.task.media_type === 'story' && !j.insights_file_id">
                        <div class="notification is-success is-inverted">
                            Your story media has been delivered, once your story ends click "Upload Insights".
                        </div>
                        <button
                            class="button"
                            :class="{ 'is-danger': delivering && delivering.id === j.id }"
                            @click="showDeliver(j)"
                        >
                            <span v-if="delivering && delivering.id === j.id">Cancel</span>
                            <span v-else>Upload Story Insights</span>
                        </button>
                    </div>

                    <div v-else class="work-room--task-status">
                        <task-status v-if="j.task.network" :status="j.status" />
                    </div>
                </div>

                <div v-if="delivering && delivering.id === j.id" class="mt-20">
                    <div class="work-room--task-feed">
                        <div>
                            <strong v-if="show_insights"> Upload Your Story Insights </strong>
                            <strong v-else>
                                Delivering
                                {{ capitalize(delivering.task.network.slug) }}
                                {{ delivering.task.media_type === 'story' ? 'Stories' : 'Tasks' }}
                            </strong>

                            <!--
                            <div v-if="canLoadFeed(delivering.task.network.slug)">
                                To deliver this task you can either paste a link your content or select it from your recent posts.
                                Choose an option below:
                                <div class="mt-15">
                                    <button class="button is-primary is-outlined" @click="showLink">Paste Link</button>
                                    <button class="button is-primary is-outlined" @click="showFeed">Load Recent Posts</button>
                                </div>
                            </div>
                            <div v-else>
                                To deliver this task you must paste a link your content. When you are ready click "Paste" link below.
                                <div class="mt-15">
                                    <button class="button is-primary is-outlined" @click="showLink">Paste Link</button>
                                </div>
                            </div>
                            -->
                        </div>

                        <div v-if="show_link" class="work-room--deliver-link">
                            <div v-if="contactNetwork(j)">
                                <g-typography class="mt-10" tag="p" size="small">
                                    Your {{ NETWORK_INFO_BY_ID[j.task.network_id].name }} post and insights should be
                                    delivered automatically, but you can still upload your post manually below.
                                </g-typography>
                            </div>

                            <g-form class="mt-10">
                                <g-form-item label="Paste the link to your post">
                                    <g-input
                                        v-model="link"
                                        :placeholder="linkPlaceholder(delivering.task.network.slug)"
                                        size="large"
                                    />
                                </g-form-item>

                                <g-form-item v-if="isMeta(j)" label="Date of Post">
                                    <g-date-picker v-model="postedAt" class="date-picker" size="large" />
                                </g-form-item>
                            </g-form>
                        </div>

                        <div v-if="show_uploaded_content" class="work-room--deliver-link">
                            <task-deliver-uploaded-content
                                :is-loading="uploadedContent.delivering"
                                :is-delivering="uploadedContent.delivering"
                                :error-message="uploadedContent.errorMessage"
                                :errors="uploadedContent.errors"
                                :network-id="delivering.task.network_id"
                                :upload-content-url="uploadUploadedContentUrl"
                                :uploaded-content-delivery-url="uploadedContentDeliveryUrl"
                                :delete-uploaded-content-url="deleteUploadedContentUrl"
                                :upload-headers="uploadHeaders"
                                :has-uploaded-files="hasUploadedFiles"
                                :uploaded-files="uploadedFiles"
                                :is-file-uploading="uploadedContent.isFileUploading"
                                :deliver-uploaded-content="uploadedContent.delivering"
                                @file-change="handleUploadedContentFileChange"
                                @reset-uploads="handleResetUploads"
                                @upload-failed="handleUploadFailed"
                                @is-ready="handleUploadedContentIsReady"
                                @deliver-uploaded-content="handleDeliveredUploadedContent"
                            />

                            <g-button
                                class="mt-10 full-width-mobile"
                                shape="round"
                                theme="live"
                                size="large"
                                type="primary"
                                :disabled="
                                    !uploadedContent.isReady ||
                                    uploadedContent.isFileUploading ||
                                    uploadedContent.delivering
                                "
                                :loading="uploadedContent.delivering"
                                @click="handleDeliverUploadedContent"
                            >
                                Deliver
                            </g-button>
                        </div>

                        <div v-if="show_upload" class="work-room--deliver-link">
                            <div
                                v-if="!showThirdParty"
                                class="notification is-primary is-inverted"
                                data-testid="ig-story-connect-banner"
                            >
                                If your story has been live for less than 24 hours you can connect your Instagram and
                                <a @click.prevent="$root.go('edit.profile')">skip this upload process</a>.
                            </div>

                            <div class="margin-bottom-normal">
                                <div class="margin-bottom-small">
                                    <g-typography tag="h3" font-weight="semibold">
                                        How to manually deliver your Instagram Story
                                    </g-typography>
                                </div>
                                <div class="margin-bottom-small">
                                    <g-typography tag="p">
                                        There are 2-steps to manually delivering your Instagram Story task after going
                                        live on Instagram.
                                    </g-typography>
                                </div>
                                <ol class="margin-bottom-normal">
                                    <g-typography tag="li">Deliver your Story media file below.</g-typography>
                                    <g-typography tag="li">
                                        Once your Story has ended, provide your Story Insights screenshot(s) and
                                        metrics.
                                    </g-typography>
                                </ol>
                                <div class="margin-bottom-small">
                                    <g-typography tag="p" size="small">
                                        Please note that Instagram Story Insights may disappear after 48 hours. For
                                        help, visit our
                                        <a :href="FAQ_DELIVERING_STORY_INSIGHTS_MANUALLY" target="_blank">
                                            help documentation </a
                                        >.
                                    </g-typography>
                                </div>
                            </div>

                            <g-upload
                                :action="deliverStoryUrl"
                                :headers="uploadHeaders"
                                :multiple="false"
                                :show-upload-list="{ showRemoveIcon: false }"
                                @change="handleStoryFileChange"
                            >
                                <g-button class="full-width-mobile" size="large" shape="round" theme="live">
                                    <g-icon name="upload" />
                                    Upload Story
                                </g-button>
                            </g-upload>
                        </div>

                        <div v-if="show_insights" class="work-room--deliver-link">
                            <div class="margin-bottom-small g-mt-3">
                                <g-typography tag="p">
                                    After your Story has ended, report your Story Insights Metrics by providing the
                                    screenshot(s) below.
                                </g-typography>
                            </div>
                            <div class="margin-bottom-small">
                                <g-typography tag="p" size="small">
                                    Please note that Instagram Story Insights may disappear after 48 hours. For help,
                                    visit our
                                    <a :href="FAQ_DELIVERING_STORY_INSIGHTS_MANUALLY" target="_blank">
                                        help documentation </a
                                    >.
                                </g-typography>
                            </div>

                            <div class="margin-bottom-normal">
                                <g-upload
                                    :action="deliverInsightsUrl"
                                    :multiple="true"
                                    :headers="uploadHeaders"
                                    :show-upload-list="{ showRemoveIcon: false }"
                                    @change="handleInsightsFileChange"
                                >
                                    <g-button class="full-width-mobile" size="large" shape="round" theme="live">
                                        <g-icon name="upload" />
                                        Upload Story Insights
                                    </g-button>
                                </g-upload>
                            </div>

                            <div class="margin-bottom-small">
                                <g-typography
                                    tag="p"
                                    font-weight="semibold"
                                    value="Report Your Story Insights Metrics"
                                />
                            </div>

                            <div class="margin-bottom-normal">
                                <g-typography tag="p" value="Enter your Story Insights metrics." />
                            </div>

                            <g-form class="margin-bottom-normal">
                                <g-form-item label="Impressions" :errors="insights.errors.impressions" required>
                                    <g-input
                                        v-model="insights.metrics.impressions"
                                        size="large"
                                        :disabled="insights.sending"
                                    />
                                </g-form-item>

                                <g-form-item label="Reach" :errors="insights.errors.reach" required>
                                    <g-input
                                        v-model="insights.metrics.reach"
                                        size="large"
                                        :disabled="insights.sending"
                                    />
                                </g-form-item>

                                <g-form-item label="Replies" :errors="insights.errors.replies" required>
                                    <g-input
                                        v-model="insights.metrics.replies"
                                        size="large"
                                        :disabled="insights.sending"
                                    />
                                </g-form-item>

                                <g-form-item label="Taps Forward" :errors="insights.errors.taps_forward" required>
                                    <g-input
                                        v-model="insights.metrics.taps_forward"
                                        size="large"
                                        :disabled="insights.sending"
                                    />
                                </g-form-item>

                                <g-form-item label="Taps Back" :errors="insights.errors.taps_back" required>
                                    <g-input
                                        v-model="insights.metrics.taps_back"
                                        size="large"
                                        :disabled="insights.sending"
                                    />
                                </g-form-item>

                                <g-form-item label="Exits" :errors="insights.errors.exits" required>
                                    <g-input
                                        v-model="insights.metrics.exits"
                                        size="large"
                                        :disabled="insights.sending"
                                    />
                                </g-form-item>
                            </g-form>

                            <g-button
                                class="full-width-mobile"
                                shape="round"
                                theme="live"
                                size="large"
                                type="primary"
                                :disabled="!insightsIsReady || insights.sending"
                                :loading="insights.sending"
                                @click="handleDeliverInsights"
                            >
                                Deliver
                            </g-button>
                        </div>

                        <div v-if="show_feed">
                            <loader v-if="loading.feed" />
                            <div v-else>
                                <instagram-feed
                                    v-if="delivering.task.network.slug === 'instagram'"
                                    ref="instagramFeed"
                                    :feed="feed"
                                    @selected="(item) => feedItemSelected(item)"
                                    @clear="() => clearSelected"
                                />
                                <youtube-feed
                                    v-if="delivering.task.network.slug === 'youtube'"
                                    ref="youtubeFeed"
                                    :feed="feed"
                                    @selected="(item) => feedItemSelected(item)"
                                    @clear="() => clearSelected"
                                />
                                <twitter-feed
                                    v-if="delivering.task.network.slug === 'twitter'"
                                    ref="twitterFeed"
                                    :feed="feed"
                                    @selected="(item) => feedItemSelected(item)"
                                    @clear="() => clearSelected"
                                />
                                <twitch-feed
                                    v-if="delivering.task.network.slug === 'twitch'"
                                    ref="twitchFeed"
                                    :feed="feed"
                                    @selected="(item) => feedItemSelected(item)"
                                    @clear="() => clearSelected"
                                />
                                <facebook-feed
                                    v-if="delivering.task.network.slug === 'facebook'"
                                    ref="facebookFeed"
                                    :feed="feed"
                                    @selected="(item) => feedItemSelected(item)"
                                    @clear="() => clearSelected"
                                />
                                <pinterest-feed
                                    v-if="delivering.task.network.slug === 'pinterest'"
                                    ref="pinterestFeed"
                                    :feed="feed"
                                    @selected="(item) => feedItemSelected(item)"
                                    @clear="() => clearSelected"
                                />
                            </div>
                        </div>

                        <div v-if="show_link || show_feed" class="mt-10">
                            <g-button
                                class="full-width-mobile"
                                shape="round"
                                theme="live"
                                size="large"
                                type="primary"
                                data-testid="deliver-button"
                                :disabled="show_link && (!link || (isMeta(j) && !postedAt))"
                                :loading="loading.deliver"
                                @click="deliver"
                            >
                                <span v-if="show_feed">Deliver Selected Content</span>
                                <span v-else>Deliver</span>
                            </g-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { capitalize } from 'lodash';
    import { message } from '@grininc/grin-ui';
    import { FAQ_DELIVERING_STORY_INSIGHTS_MANUALLY } from '../../../../app-v2/constants/faqs';
    import { NETWORK_INFO_BY_ID } from '../../../../app-v2/constants/networks';
    import InstagramFeed from './feeds/InstagramFeed.vue';
    import YoutubeFeed from './feeds/YoutubeFeed.vue';
    import TwitterFeed from './feeds/TwitterFeed.vue';
    import FacebookFeed from './feeds/FacebookFeed.vue';
    import TwitchFeed from './feeds/TwitchFeed.vue';
    import PinterestFeed from './feeds/PinterestFeed.vue';
    import TaskStatus from './TaskStatus.vue';
    import TaskDeliverUploadedContent from '../../../../app-v2/components/data-views/activation-briefs/partials/tasks/TaskDeliverUploadedContent.vue';
    import UploadMixin from '../../../../app-v2/components/providers/mixins/UploadMixin';
    import StoryInsightsMixin from '../../../../app-v2/components/providers/mixins/StoryInsightsMixin';
    import UploadedContentMixin from '../../../../app-v2/components/providers/mixins/UploadedContentMixin';
    import useStoryInsights from '../../../composables/use-story-insights';
    import useBrands from '../../../composables/use-brands';
    import { useFeatureFlagsStore } from '../../../../stores';

    export default {
        name: 'JobTasks',
        props: {
            campaign: {
                type: Object,
                default: null
            },
            jobs: {
                type: Array,
                required: true
            },
            deliverable: {
                type: Boolean,
                default: false
            }
        },
        components: {
            TaskStatus,
            InstagramFeed,
            YoutubeFeed,
            TwitterFeed,
            FacebookFeed,
            TwitchFeed,
            PinterestFeed,
            TaskDeliverUploadedContent
        },
        mixins: [UploadMixin, StoryInsightsMixin, UploadedContentMixin],
        data() {
            return {
                loading: {
                    deliver: false,
                    feed: false,
                    brandNetworks: false
                },
                feed: null,
                link: null,
                postedAt: null,
                delivering: null,
                show_req: [],
                show_link: false,
                show_feed: false,
                show_upload: false,
                show_insights: false,
                show_uploaded_content: false,
                selected: null,
                FAQ_DELIVERING_STORY_INSIGHTS_MANUALLY,
                NETWORK_INFO_BY_ID
            };
        },

        computed: {
            can_deliver() {
                if (this.campaign.type !== 'deal') {
                    return true;
                }

                return this.campaign.can_deliver_content;
            },
            deliverStoryUrl() {
                return `/contact/${this.$root.contact_id}/jobs/${this.delivering?.id}/deliver/story`;
            },
            deliverInsightsUrl() {
                return `/contact/${this.$root.contact_id}/jobs/${this.delivering?.id}/deliver/story/insights`;
            },
            uploadedContentDeliveryUrl() {
                return `/api/v1/campaigns/${this.campaign?.id}/jobs/${this.delivering?.id}/uploaded-content`;
            },
            uploadUploadedContentUrl() {
                return `/contact/${this.$root.contact_id}/jobs/${this.delivering?.id}/uploaded-content/upload?network_id=${this.delivering?.task?.network_id}`;
            },
            deleteUploadedContentUrl() {
                return `/contact/${this.$root.contact_id}/jobs/${this.delivering?.id}/uploaded-content/`;
            },
            showThirdParty() {
                return true;
            }
        },

        methods: {
            capitalize(str) {
                return capitalize(str);
            },
            linkPlaceholder(network) {
                switch (network) {
                    case 'instagram':
                        return 'https://www.instagram.com/p/gY8rhj';
                    case 'twitter':
                        return 'https://www.twitter.com/username/status/9384490569';
                    case 'youtube':
                        return 'https://www.youtube.com/watch?v=zSJdQy5yNTQ';
                    case 'pinterest':
                        return 'https://www.pinterest.com/pin/491596115568476973';
                    case 'facebook':
                        return 'https://www.facebook.com/PageName/posts/1998496613768646';
                    case 'twitch':
                        return 'https://www.twitch.tv/videos/207238962';
                }
            },
            canLoadFeed(network) {
                switch (network) {
                    case 'instagram':
                        return true;
                    case 'twitter':
                        return true;
                    case 'youtube':
                        return true;
                    case 'pinterest':
                        return false;
                    case 'facebook':
                        return false;
                    case 'twitch':
                        return false;
                }
            },
            showDeliver(job) {
                this.show_feed = false;
                this.show_uploaded_content = false;
                this.handleResetUploads();

                if (job.task.media_type === 'story') {
                    this.show_link = false;

                    if (job.status === 'Delivered' || job.status === 'Pending') {
                        this.show_upload = false;
                        this.show_insights = true;
                    } else {
                        this.show_insights = false;
                        this.show_upload = true;
                    }
                } else if (this.showUploadedContentDelivery(job, this.brandNetworks)) {
                    this.show_uploaded_content = true;
                    this.show_upload = false;
                    this.show_link = false;
                    this.show_insights = false;
                } else {
                    this.show_upload = false;
                    this.show_link = true;
                    this.show_insights = false;
                }

                if (this.delivering && this.delivering.id === job.id) {
                    return (this.delivering = null);
                }
                this.delivering = job;
            },
            canShowDeliver(job) {
                if (job.task.media_type === 'story') {
                    return job.task.network && job.status !== 'Delivered' && job.status !== 'Pending';
                } else {
                    return job.status !== 'Delivered' && job.task.network;
                }
            },
            showLink() {
                this.show_feed = false;
                this.show_link = true;
            },
            async showFeed() {
                this.show_link = false;
                this.show_feed = true;
                this.loading.feed = true;

                this.$http.get(`/contact/${this.$root.contact_id}/jobs/${this.delivering.id}/feed`).then(
                    (res) => {
                        this.feed = res.data;
                        this.loading.feed = false;
                        this.$emit('feed-loaded');
                    },
                    () => {
                        this.loading.feed = false;
                        this.$notifyError(
                            `Could not load ${this.delivering.task.network} feed, please deliver via link`
                        );
                    }
                );
            },
            feedItemSelected(item) {
                this.selected = item;
            },
            clearSelected() {
                this.selected = null;
            },
            deliver() {
                this.loading.deliver = true;

                const data = this.show_link
                    ? {
                          link: this.link,
                          posted_at: this.postedAt
                      }
                    : { content: this.selected };

                this.$http.post(`/contact/${this.$root.contact_id}/jobs/${this.delivering.id}/deliver`, data).then(
                    () => {
                        this.$root.reloadContact('jobs');
                        this.$root.reloadContact('proposals');

                        this.loading.deliver = false;
                        this.link = null;
                        this.postedAt = null;
                        this.selected = null;
                        this.delivering = null;

                        this.$notifySuccess('Nice! Your sponsorship task has been delivered');
                    },
                    () => {
                        this.loading.deliver = false;
                        this.link = null;
                        this.postedAt = null;
                        this.$notifyError(
                            'There was a problem delivering your content, please try again or contact support'
                        );
                    }
                );
            },
            handleStoryFileChange(info) {
                this.handleUploadChange(info, () => {
                    this.$root.reloadContact('jobs');
                    this.$root.reloadContact('proposals');
                    this.showDeliver(this.delivering);

                    message.success('Nice! Your sponsorship task has been delivered');
                });
            },
            handleInsightsFileChange(info) {
                this.handleUploadChange(
                    info,
                    () => {},
                    () => {},
                    true
                );
            },
            async handleDeliverInsights() {
                if (!this.insightsIsReady) {
                    return;
                }

                this.insights.sending = true;
                this.loading.deliver = true;

                try {
                    await useStoryInsights().postCampaignInsights(this.delivering.proposal_id, this.delivering.id, {
                        files: this.uploadedFiles,
                        metrics: this.getMetricsWithoutCommas()
                    });

                    message.success('Your Story Insights have been delivered!');
                    this.resetInsights();
                    this.$root.reloadContact('jobs');
                    this.$root.reloadContact('proposals');
                    this.showDeliver(this.delivering);
                } catch (e) {
                    message.error(
                        'There was an error while trying to deliver your story insights, please try again later.'
                    );
                }

                this.insights.sending = false;
                this.loading.deliver = false;
            },
            handleResetUploads() {
                this.handleResetUploadedFiles();
            },
            async loadBrandNetworks() {
                this.loading.brandNetworks = true;

                try {
                    const response = await useBrands().getSocialAccounts(this.campaign?.brand_id);
                    this.brandNetworks = response.data;
                } catch (e) {
                    message.error('There was an error loading your deliverables, please try again later.');
                }

                this.loading.brandNetworks = false;
            }
        },
        created() {
            this.loadBrandNetworks();
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    ::v-deep .ant-upload.ant-upload-select {
        display: block;
        margin-bottom: $g-spacing-5;
    }

    ::v-deep .ant-form-item {
        padding-bottom: $g-spacing-5;
        margin-bottom: 0;
    }

    .date-picker {
        width: 100%;
    }

    @media (max-width: $g-screen-size-xs) {
        .full-width-mobile {
            width: 100%;
        }
    }
</style>
